<template>
  <div>
    <McGraphqlEditableTable
      class="cdm-device-status-table"
      trackBy="vehicleId"
      :fields="listDeviceStatusFields"
      :apiClient="query"
      :canCreate="false"
      :canDelete="false"
      :canEdit="isEditable"
      :hideCheckbox="true"
      :isResponsive="false"
      :hasStickyAction="true"
    >
      <template #cell(lastActiveOn)="{ value }">
        <template v-if="value">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>
        <template v-else> - </template>
      </template>
      <template #cell(address)="{ item, value }">
        <div v-if="value" class="address-wrapper">
          <span class="address-text" @click="openGoogleMaps(item.latitude, item.longitude)">
            {{ value }}
          </span>
          <span
            v-b-tooltip.hover
            class="address-copy-icon"
            :title="$t('global.field.copy')"
            @click="copyAddress(getLinkGoogleMaps(item.latitude, item.longitude))"
          >
            <i class="mc-fa icon-outline_copy"></i>
          </span>
        </div>
        <template v-else> - </template>
      </template>
      <template #cell(isDoorOpen)="{ value }">
        <div class="d-flex">
          <template v-if="value">
            <div class="cdm-badge cdm-badge__warning">
              {{ $t('cdmSecurityMonitoring.status.open') }}
            </div>
          </template>
          <template v-else>
            <div class="cdm-badge cdm-badge__success">
              {{ $t('cdmSecurityMonitoring.status.closed') }}
            </div>
          </template>
        </div>
      </template>
      <template #cell(set-buzzer-btn)="{ item }">
        <McButton
          icon="icon-outline_settings"
          iconPosition="left"
          size="sm"
          variant="secondary"
          text=""
          @click="handleClickBuzzerSetting(item)"
        />
      </template>
      <template #cell(liveStreamBtn)="{ item }">
        <McButton
          class="cdm-btn"
          icon="icon-outline_video"
          iconPosition="left"
          size="sm"
          :text="$t('cdmSecurityMonitoring.action.liveStreaming')"
          @click="handleClickLiveStream(item)"
        />
      </template>
    </McGraphqlEditableTable>

    <ModalBuzzerSetting
      :vehicleId="selectedVehicleId"
      @onResetVehicleId="selectedVehicleId = null"
      @onClose="handleCloseModalBuzzer"
    />
  </div>
</template>
<script>
// Helper
import { formatIndexDate } from '@/helpers/tzDate'
import { Helper } from '@/mixin/helper'

// Components
import McGraphqlEditableTable from '@/components/McGraphqlEditableTable'
import McButton from '@/components/McButton'
import ModalBuzzerSetting from '../components/ModalBuzzerSetting'

// Libs
import { listDeviceStatusFields } from '../libs/list-device-status-fields'
import { DOOR_STATUS } from '../libs/constants'
import { openGoogleMaps, getLinkGoogleMaps } from '../libs/helper'

// Services
import { GET_CDM_DEVICE_STATUSES } from '@/services/api/cdm/deviceStatuses'
import { VEHICLE_MUTATION_UPDATE } from '@/services/api/master/fleet'

export default {
  components: {
    McGraphqlEditableTable,
    McButton,
    ModalBuzzerSetting,
  },
  mixins: [listDeviceStatusFields, Helper],
  data() {
    return {
      selectedVehicleId: null,
    }
  },
  computed: {
    query() {
      return {
        get: async (filter) => {
          try {
            const formattedFilter = {
              parentOnly: true,
              vehicleCategory: ['CDM'],
              pagination: filter.pagination,
              withAddress: true,
            }

            const result = await this.$apollo.query({
              query: GET_CDM_DEVICE_STATUSES,
              fetchPolicy: 'no-cache',
              variables: {
                filter: formattedFilter,
              },
            })

            if (!result.data.vehicleStatusesPaginated.isSuccessful) {
              return {
                data: [],
                pagination: {
                  total: 0,
                  page: 1,
                  show: 10,
                },
              }
            }

            const response = result.data.vehicleStatusesPaginated.vehicleStatusesPaginated

            const transformedData = response.data.map((item) => {
              let temperature = null
              if (Array.isArray(item.temperature)) {
                temperature = item.temperature[0] ?? null
              } else {
                temperature = item.temperature ?? null
              }

              return {
                vehicleId: item.vehicleId,
                licensePlate: item.licensePlate,
                lastActiveOn: item.lastPacket,
                longitude: item.longitude,
                latitude: item.latitude,
                isDoorOpen: item.door1Status === DOOR_STATUS.OPEN,
                temperature: temperature,
                battery: item.battery,
                powerSupply: item.accuVoltage,
                address: item.address,
                children: {
                  id: item.vehicle?.children?.[0]?.id,
                  licensePlate: item.licensePlate,
                  vehicleType: item.vehicle?.vehicleType,
                  vehicle2Vgroup: item.vehicle?.vehicle2Vgroup,
                },
                startSchedule: item.vehicle?.sensorTrigStartTime || null,
                endSchedule: item.vehicle?.sensorTrigEndTime || null,
              }
            })

            return {
              data: transformedData,
              pagination: response.pagination,
            }
          } catch (error) {
            console.error('Error fetching device statuses:', error)
            return {
              data: [],
              pagination: {
                total: 0,
                page: 1,
                show: 10,
              },
            }
          }
        },
        update: async (id, oldData, newData) => {
          try {
            const input = {
              id: id,
              oldData: {
                sensorTrigStartTime: oldData.startSchedule || null,
                sensorTrigEndTime: oldData.endSchedule || null,
              },
              newData: {
                sensorTrigStartTime: newData.startSchedule + ':00' || null,
                sensorTrigEndTime: newData.endSchedule + ':00' || null,
                scheduledAlerts: [122, 4, 19],
                scheduledSensorTrigger: [25],
              },
            }

            const result = await this.$apollo.mutate({
              mutation: VEHICLE_MUTATION_UPDATE,
              variables: {
                input: input,
              },
            })

            if (!result.data.updateVehicle.isSuccessful) {
              this.$mcToast.error(this.$t('global.notification.updateFailed'))
              return
            }

            return result.data.updateVehicle.isSuccessful
          } catch (error) {
            console.error('Error updating vehicle schedule:', error)
            this.$mcToast.error(this.$t('global.notification.updateFailed'))
            return
          }
        },
      }
    },
    isEditable() {
      return this.getAccess() !== 'USER'
    },
  },
  methods: {
    formatIndexDate,
    openGoogleMaps,
    getLinkGoogleMaps,
    copyAddress(text) {
      navigator.clipboard.writeText(text)
      this.$mcToast.success(this.$t('global.action.copySuccess'))
    },
    handleClickBuzzerSetting(item) {
      this.selectedVehicleId = item?.vehicleId ?? null
      this.$bvModal.show('modal-buzzer-setting')
    },
    handleCloseModalBuzzer() {
      this.selectedVehicleId = null
    },
    handleClickLiveStream(item) {
      if (
        item.children?.id !== null &&
        item.children?.id !== undefined &&
        item.children?.licensePlate !== null &&
        item.children?.licensePlate !== undefined
      ) {
        localStorage.setItem(
          'filter-live-stream-v2',
          JSON.stringify({
            motionStatus: [],
            vehicleId: [{ ...item.children }],
            videoStatus: [],
          })
        )
      }

      window.open(
        this.$router.resolve({
          name: 'Live Streaming Trackvision',
          query: {
            vehicleId: item?.children?.id,
          },
        }).href,
        '_blank'
      )
    },
  },
}
</script>
<style lang="scss" scoped>
.cdm-device-status-table {
  :deep(td) {
    vertical-align: middle;
  }

  :deep(.address-wrapper) {
    display: flex;
    justify-content: space-between;
    gap: 10px;

    .address-text {
      cursor: pointer;

      &:hover {
        color: $mep-primary-400;
        text-decoration: underline;
      }
    }

    .address-copy-icon {
      font-size: 20px;
      cursor: pointer;

      &:hover {
        color: $mep-primary-400;
      }
    }
  }

  .cdm-badge {
    padding: 4px 8px;
    border-radius: 4px;

    &__success {
      background-color: $green-100;
      color: $green-500;
    }

    &__warning {
      background-color: $yellow-100;
      color: $yellow-600;
    }
  }

  .cdm-btn :deep(.mc-button) {
    padding: 4px 8px;

    .mc-icon {
      display: flex;
      align-items: center;

      &::before {
        font-size: 16px;
      }
    }
  }

  :deep(.formulate-input .formulate-input-element.input-md .mx-datepicker input[name='date']) {
    padding: 4px 8px !important;
    height: 30px !important;
  }
}
</style>
